/**
 * Free Tiered Promo — 3-tier cart upsell handler.
 * Tier 1: free shipping (no product). Tier 2 / Tier 3: free product variants.
 *
 * Responsibilities:
 *  - Click handler for "Add the FREE item" CTA (delegated, survives re-renders)
 *  - Tier 3 mutex: removes tier 2 product before adding tier 3 when enabled
 *  - Periodic check: removes any free reward product whose threshold is no longer met
 *  - Dedup: collapses duplicate or qty>1 free items down to a single unit
 */

class FreeTieredPromo {
  constructor() {
    this.config = null;
    this.isMutating = false;
    this.isChecking = false;
    this.periodicCheckInterval = null;
    this.cartUpdateUnsubscriber = null;
    this.init();
  }

  init() {
    this.loadConfig();
    this.attachListeners();
    this.startPeriodicCheck();
  }

  loadConfig() {
    const configScript = document.querySelector('[data-free-tiered-promo-config]');
    if (!configScript) {
      this.config = null;
      return;
    }
    try {
      this.config = JSON.parse(configScript.textContent);
    } catch (e) {
      console.error('[free-tiered-promo] failed to parse config', e);
      this.config = null;
    }
  }

  attachListeners() {
    if (!this._documentClickBound) {
      this._documentClickBound = this.onDocumentClick.bind(this);
      document.addEventListener('click', this._documentClickBound);
    }

    if (!this._cartUpdatedBound) {
      this._cartUpdatedBound = this.onCartUpdated.bind(this);
      document.addEventListener('cart:updated', this._cartUpdatedBound);
    }

    if (window.theme && window.theme.pubsub && !this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber = window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdated.bind(this)
      );
    }
  }

  startPeriodicCheck() {
    if (this.periodicCheckInterval) clearInterval(this.periodicCheckInterval);
    this.periodicCheckInterval = setInterval(() => {
      this.reconcileCart();
    }, 1000);
  }

  onDocumentClick(event) {
    const button = event.target.closest('[data-add-tiered-free-product]');
    if (!button) return;
    event.preventDefault();
    this.onAddFreeProduct(button);
  }

  onCartUpdated() {
    this.loadConfig();
  }

  async onAddFreeProduct(button) {
    if (this.isMutating) return;

    const variantId = button.getAttribute('data-variant-id');
    const tier = parseInt(button.getAttribute('data-tier'), 10);
    if (!variantId || (tier !== 2 && tier !== 3)) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    this.isMutating = true;

    try {
      const cart = await this.fetchCart();

      // Mutex: tier 3 add must clear tier 2 first.
      if (
        tier === 3 &&
        this.config &&
        this.config.mutex &&
        this.config.tier2VariantId
      ) {
        await this.removeVariant(cart, this.config.tier2VariantId);
      }

      // Skip add if already present.
      const refreshedCart = await this.fetchCart();
      if (!this.cartContainsVariant(refreshedCart, variantId)) {
        await this.addVariant(variantId);
      }

      this.refreshSurface();
    } catch (err) {
      console.error('[free-tiered-promo] add failed', err);
    } finally {
      this.isMutating = false;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  async reconcileCart() {
    if (!this.config) return;
    if (this.isMutating || this.isChecking) return;

    const { tier2VariantId, tier3VariantId, tier2Threshold, tier3Threshold } = this.config;
    if (!tier2VariantId && !tier3VariantId) return;

    this.isChecking = true;
    try {
      const cart = await this.fetchCart();
      const subtotal = this.calculateEligibleSubtotal(cart, [tier2VariantId, tier3VariantId]);

      let mutated = false;

      // Dedup / normalize tier 2 and tier 3 reward items first (any qty>1 or duplicate lines collapse to 1).
      for (const variantId of [tier2VariantId, tier3VariantId]) {
        if (!variantId) continue;
        const lines = this.findLinesForVariant(cart, variantId);
        if (lines.length > 1) {
          // Remove all extra duplicate lines (line numbers shift after each removal — reverse-sort).
          for (const line of lines.slice(1).sort((a, b) => b.line - a.line)) {
            await this.removeLine(line.line);
            mutated = true;
          }
        } else if (lines.length === 1 && lines[0].quantity > 1) {
          await this.setLineQuantity(lines[0].line, 1);
          mutated = true;
        }
      }

      // Re-fetch if we touched anything, so threshold checks see post-mutation state.
      const checkCart = mutated ? await this.fetchCart() : cart;
      const checkSubtotal = mutated
        ? this.calculateEligibleSubtotal(checkCart, [tier2VariantId, tier3VariantId])
        : subtotal;

      // Remove reward products whose threshold is no longer met.
      if (tier2VariantId && tier2Threshold > 0 && checkSubtotal < tier2Threshold) {
        if (this.cartContainsVariant(checkCart, tier2VariantId)) {
          await this.removeVariant(checkCart, tier2VariantId);
          mutated = true;
        }
      }
      if (tier3VariantId && tier3Threshold > 0 && checkSubtotal < tier3Threshold) {
        // Re-fetch in case tier 2 removal shifted lines.
        const cart2 = await this.fetchCart();
        if (this.cartContainsVariant(cart2, tier3VariantId)) {
          await this.removeVariant(cart2, tier3VariantId);
          mutated = true;
        }
      }

      if (mutated) this.refreshSurface();
    } catch (err) {
      console.error('[free-tiered-promo] reconcile failed', err);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Sum cart items, excluding gift wrapping, gift cards, and reward variants themselves.
   * Matches the Liquid snippet's subtotal logic so JS & Liquid agree on threshold state.
   */
  calculateEligibleSubtotal(cart, rewardVariantIds) {
    let subtotal = cart.total_price;
    const rewards = new Set(rewardVariantIds.filter(Boolean).map(String));

    // Gift wrapping linklist lookup.
    let giftWrapVariantId = null;
    if (
      window.linklists &&
      window.linklists['gift-wrapping'] &&
      window.linklists['gift-wrapping'].links.length > 0
    ) {
      const link = window.linklists['gift-wrapping'].links[0];
      if (link && link.object && link.object.variants && link.object.variants.length > 0) {
        giftWrapVariantId = String(link.object.variants[0].id);
      }
    }

    for (const item of cart.items) {
      const vid = String(item.variant_id);
      if (vid === giftWrapVariantId) {
        subtotal -= item.line_price;
        continue;
      }

      const isGiftCard =
        item.product_type === 'Gift Card' ||
        (item.handle && item.handle.toLowerCase().includes('gift-card')) ||
        (item.product_title && item.product_title.toLowerCase().includes('gift card'));
      if (isGiftCard) {
        subtotal -= item.line_price;
        continue;
      }

      if (rewards.has(vid)) {
        subtotal -= item.line_price;
      }
    }

    return Math.max(0, subtotal);
  }

  cartContainsVariant(cart, variantId) {
    const target = String(variantId);
    return cart.items.some((item) => String(item.variant_id) === target);
  }

  /** Returns [{line, quantity, key}] for all cart lines matching variantId (1-based line numbers). */
  findLinesForVariant(cart, variantId) {
    const target = String(variantId);
    const matches = [];
    cart.items.forEach((item, idx) => {
      if (String(item.variant_id) === target) {
        matches.push({ line: idx + 1, quantity: item.quantity, key: item.key });
      }
    });
    return matches;
  }

  async fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async addVariant(variantId) {
    const url = `${(window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add'}.js`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'add failed');
    }
    return res.json();
  }

  async removeVariant(cart, variantId) {
    const lines = this.findLinesForVariant(cart, variantId);
    if (lines.length === 0) return;
    // Remove from highest line number first so lower line indices stay valid.
    for (const line of lines.sort((a, b) => b.line - a.line)) {
      await this.removeLine(line.line);
    }
  }

  async removeLine(line) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity: 0 }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'remove failed');
    }
    return res.json();
  }

  async setLineQuantity(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'quantity update failed');
    }
    return res.json();
  }

  refreshSurface() {
    const isCartPage = document.body.classList.contains('template-cart');
    if (isCartPage) {
      window.location.reload();
      return;
    }
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      cartDrawer.refresh();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'free-tiered-promo' } }));
    }
  }

  destroy() {
    if (this.periodicCheckInterval) clearInterval(this.periodicCheckInterval);
    if (this._documentClickBound) document.removeEventListener('click', this._documentClickBound);
    if (this._cartUpdatedBound) document.removeEventListener('cart:updated', this._cartUpdatedBound);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.freeTieredPromo) {
    window.freeTieredPromo.destroy();
  }
  window.freeTieredPromo = new FreeTieredPromo();
});

window.addEventListener('beforeunload', () => {
  if (window.freeTieredPromo) window.freeTieredPromo.destroy();
});
